import { useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  CircularProgress,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { useService } from '@old/hooks/use-service';
import { externalSharesService } from '@old/services/domain/base/silobag/share/external-shares';
import type { ExternalShareEntry } from '@old/services/domain/base/silobag/share/external-shares.types';
import i18n from '../../../../libs/language';

function getPermissionLabel(entry: ExternalShareEntry): string {
  if (entry.full_permission) return i18n.t('app.silobag_share.permission_full');
  if (entry.write_permission) return i18n.t('app.silobag_share.permission_write');
  if (entry.read_permission) return i18n.t('app.silobag_share.permission_read');
  return i18n.t('app.silobag_share.permission_none');
}

const tableHeaderStyle = {
  fontWeight: 600,
  fontSize: '13px',
  color: '#6B7280',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  padding: '8px 16px',
  textAlign: 'left' as const,
};

const tableCellStyle = {
  fontSize: '14px',
  color: '#1F2937',
  padding: '10px 16px',
  borderBottom: '1px solid #F3F4F6',
  textAlign: 'left' as const,
};

type ExternalSharesSectionProps = {
  silobagId: string;
  countrysideName?: string;
};

export function ExternalSharesSection({ silobagId, countrysideName }: ExternalSharesSectionProps) {
  const { summary, isLoading, trigger } = useService(externalSharesService);

  useEffect(() => {
    if (silobagId) {
      trigger({ query: { silobagId } });
    }
  }, [silobagId]);

  // Don't render anything while loading or if there are no external shares
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={30} sx={{ color: '#FF8C00' }} />
      </Box>
    );
  }

  const silobagShares = summary?.silobag_shares ?? [];
  const countrysideShares = summary?.countryside_shares ?? [];
  const hasExternalShares = silobagShares.length > 0 || countrysideShares.length > 0;

  if (!hasExternalShares) return null;

  return (
    <Accordion
      id="external-shares-section"
      elevation={0}
      sx={{
        backgroundColor: 'white',
        borderRadius: '10px !important',
        '&:before': { display: 'none' },
        border: '1px solid #E5E7EB',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 1,
          },
        }}
      >
        <PeopleOutlineIcon sx={{ color: '#FF8C00', fontSize: 22 }} />
        <Typography fontWeight={600} fontSize="15px" color="#1F2937">
          {i18n.t('app.silobag_share.external_shares_title')}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: '0 16px 16px' }}>
        {/* Silobag direct shares */}
        {silobagShares.length > 0 && (
          <Box mb={countrysideShares.length > 0 ? 3 : 0}>
            <Typography fontWeight={600} fontSize="14px" color="#374151" mb={1} ml={2}>
              {i18n.t('app.silobag_share.shared_silobags')}
            </Typography>
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                '& thead': { backgroundColor: '#F9FAFB' },
              }}
            >
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>{i18n.t('app.silobag_share.external_email')}</th>
                  <th style={tableHeaderStyle}>{i18n.t('app.silobag_share.external_organization')}</th>
                  <th style={tableHeaderStyle}>{i18n.t('app.silobag_share.permission_level')}</th>
                </tr>
              </thead>
              <tbody>
                {silobagShares.map((share, idx) => (
                  <tr key={`sb-share-${idx}`}>
                    <td style={tableCellStyle}>{share.email}</td>
                    <td style={tableCellStyle}>{share.organization_name ?? '—'}</td>
                    <td style={tableCellStyle}>
                      <PermissionChip label={getPermissionLabel(share)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        )}

        {/* Countryside inherited shares */}
        {countrysideShares.length > 0 && (
          <Box>
            <Typography fontWeight={600} fontSize="14px" color="#374151" mb={1} ml={2}>
              {i18n.t('app.silobag_share.shared_countrysides')}
            </Typography>
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                '& thead': { backgroundColor: '#F9FAFB' },
              }}
            >
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>{i18n.t('app.silobag_share.external_email')}</th>
                  <th style={tableHeaderStyle}>{i18n.t('app.silobag_share.external_organization')}</th>
                  <th style={tableHeaderStyle}>{i18n.t('app.silobag_share.permission_level')}</th>
                  <th style={tableHeaderStyle}>{i18n.t('app.countryside')}</th>
                </tr>
              </thead>
              <tbody>
                {countrysideShares.map((share, idx) => (
                  <tr key={`cs-share-${idx}`}>
                    <td style={tableCellStyle}>{share.email}</td>
                    <td style={tableCellStyle}>{share.organization_name ?? '—'}</td>
                    <td style={tableCellStyle}>
                      <PermissionChip label={getPermissionLabel(share)} />
                    </td>
                    <td style={tableCellStyle}>{countrysideName ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function PermissionChip({ label }: { label: string }) {
  const colorMap: Record<string, { bg: string; text: string }> = {
    [i18n.t('app.silobag_share.permission_read')]: { bg: '#DBEAFE', text: '#1E40AF' },
    [i18n.t('app.silobag_share.permission_write')]: { bg: '#FEF3C7', text: '#92400E' },
    [i18n.t('app.silobag_share.permission_full')]: { bg: '#D1FAE5', text: '#065F46' },
  };

  const colors = colorMap[label] ?? { bg: '#F3F4F6', text: '#6B7280' };

  return (
    <Stack
      component="span"
      direction="row"
      alignItems="center"
      sx={{
        display: 'inline-flex',
        backgroundColor: colors.bg,
        color: colors.text,
        borderRadius: '12px',
        padding: '2px 10px',
        fontSize: '13px',
        fontWeight: 500,
      }}
    >
      {label}
    </Stack>
  );
}
